/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { JSX } from "react";

import { SystemDialogContent } from "@cms/components/data/Dialog";
import { SystemTextInput } from "@cms/components/data/TextInput";
import { useDialog } from "@cms/providers/DialogProvider";
import { useSnackbar } from "@cms/providers/SnackbarProvider";
import { isValidHtml } from "@dl/declarations";
import { $createParagraphNode, $getSelection, $insertNodes, $isRangeSelection, LexicalEditor } from "lexical";
import { useEffect, useRef, useState } from "react";
import { $createOEmbedNode } from "../../nodes/OEmbedNode";

export function InsertRawHTMLDialog({
  activeEditor,
}: {
  activeEditor: LexicalEditor;
}): JSX.Element {
  const { closeDialog } = useDialog();
  const { showSnackbar } = useSnackbar();

  const inputRef = useRef<HTMLInputElement>(null);
  const [html, setHtml] = useState('');

  const onClick = () => {
    if (!html) {
      return
    }

    const isValid = isValidHtml(html);

    if (!isValid) {
      showSnackbar('Invalid HTML. Please enter valid HTML.', 'error');

      return;
    }

    activeEditor.update(() => {
      const selection = $getSelection();

      if (
        selection &&
        $isRangeSelection(selection)
        && selection.isCollapsed()
      ) {
        const node = $createOEmbedNode('', html, '');
        selection.insertNodes([
          node,
          $createParagraphNode()
        ]);
      } else {
        // Fallback: insert at the end of the document
        const node = $createOEmbedNode('', html, '');
        $insertNodes([
          node,
          $createParagraphNode()
        ]);
      }
    });

    closeDialog();
  };

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  return (
    <SystemDialogContent
      dialogProps={{
        title: 'Insert Raw HTML',
        maxWidth: 'md'
      }}
      content={{
        component: (
          <>
            <SystemTextInput
              autoFocus
              name="cs-raw-html"
              placeholder={'Enter HTML'}
              onChange={(e) => setHtml(e.target.value)}
              value={html}
              inputSize="full"
              multiline
              rows={5}
              inputRef={inputRef}
            />
          </>
        )
      }}
      actionsProps={{
        submit: {
          onClick: onClick
        },
        cancel: {
          onClick: closeDialog
        }
      }}
    />
  )
}
